if (!customElements.get('age-verifier')) {
  customElements.define(
    'age-verifier',
    class AgeVerifier extends ModalElement {
      constructor() {
        super();

        this.ageAccepted = false;
        this.backgroundState = new Map();
        this.backgroundObserver = null;
        this.focusGuardFrame = null;
        this.onAgeAccept = this.onAgeAccept.bind(this);
        this.onAgeDecline = this.onAgeDecline.bind(this);
        this.onAgeSelectionReturn = this.onAgeSelectionReturn.bind(this);
        this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
        this.onWindowBlur = this.onWindowBlur.bind(this);

        this.init();
      }

      connectedCallback() {
        super.connectedCallback();

        this.ageVerifierAbortController?.abort();
        this.ageVerifierAbortController = new AbortController();

        this.setAttribute('aria-modal', 'true');
        this.defaultLabelledBy ||= this.getAttribute('aria-labelledby');

        if (!Shopify.designMode) {
          const closeButton = this.querySelector('.drawer__close');
          if (closeButton) {
            closeButton.disabled = true;
            closeButton.hidden = true;
            closeButton.tabIndex = -1;
            closeButton.setAttribute('aria-hidden', 'true');
          }

          const gestureHeader = this.querySelector('gesture-element[tabindex]');
          if (gestureHeader) gestureHeader.tabIndex = -1;
        }

        if (this.acceptButton) {
          this.acceptButton.dataset.ageAccept = 'true';
          this.acceptButton.addEventListener('click', this.onAgeAccept, {
            capture: true,
            signal: this.ageVerifierAbortController.signal,
          });
        }

        this.declineButton?.addEventListener('click', this.onAgeDecline, {
          signal: this.ageVerifierAbortController.signal,
        });
        this.returnButton?.addEventListener('click', this.onAgeSelectionReturn, {
          signal: this.ageVerifierAbortController.signal,
        });
        document.addEventListener('focusin', this.onDocumentFocusIn, {
          capture: true,
          signal: this.ageVerifierAbortController.signal,
        });
        window.addEventListener('blur', this.onWindowBlur, {
          signal: this.ageVerifierAbortController.signal,
        });
      }

      disconnectedCallback() {
        this.ageVerifierAbortController?.abort();
        this.ageVerifierAbortController = null;
        cancelAnimationFrame(this.focusRecoveryFrame);
        this.stopFocusGuard();
        this.restoreBackground();
        super.disconnectedCallback();
      }

      get shouldLock() {
        return true;
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return 1;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get cookieName() {
        return 'concept:age-verifier';
      }

      get acceptButton() {
        return this.querySelector('.cookie__buttons button[aria-controls="AgeVerifier"]');
      }

      get declineButton() {
        return this.querySelector('[data-age-decline]');
      }

      get selectionPanel() {
        return this.querySelector('[data-age-selection]');
      }

      get declinePanel() {
        return this.querySelector('[data-age-decline-status]');
      }

      get declineHeading() {
        return this.querySelector('[data-age-decline-heading]');
      }

      get returnButton() {
        return this.querySelector('[data-age-selection-return]');
      }

      get declined() {
        return this.dataset.ageState === 'declined';
      }

      get focusElement() {
        if (this.declined) return this.returnButton || this.declineHeading || this;
        return this.acceptButton || this;
      }

      get sectionRoot() {
        return this.closest('.shopify-section') || this;
      }

      init() {
        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
      }

      onAgeAccept(event) {
        if (Shopify.designMode) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!this.open) {
          this.ageAccepted = false;
          return;
        }

        this.ageAccepted = true;
        Promise.resolve(this.hide()).then(() => {
          setTimeout(() => {
            if (this.open || !this.hidden) {
              this.ageAccepted = false;
              this.removeCookie(this.cookieName);
              this.startFocusGuard();
              this.recoverFocus();
            }
          });
        });
      }

      onAgeDecline(event) {
        event.preventDefault();
        this.ageAccepted = false;
        this.showDeclineState();
      }

      onAgeSelectionReturn(event) {
        event.preventDefault();
        this.showSelectionState({ focus: true });
      }

      get focusTrapActive() {
        return this.open && !this.ageAccepted && !Shopify.designMode;
      }

      recoverFocus() {
        if (!this.focusTrapActive) return;

        cancelAnimationFrame(this.focusRecoveryFrame);
        this.focusRecoveryFrame = requestAnimationFrame(() => {
          if (!this.focusTrapActive || this.contains(document.activeElement)) return;
          this.focusElement?.focus({ preventScroll: true });
        });
      }

      startFocusGuard() {
        this.stopFocusGuard();

        const guard = () => {
          this.focusGuardFrame = null;
          if (!this.focusTrapActive) return;

          if (document.hasFocus() && !this.contains(document.activeElement)) {
            this.focusElement?.focus({ preventScroll: true });
          }

          if (this.focusTrapActive) {
            this.focusGuardFrame = requestAnimationFrame(guard);
          }
        };

        this.focusGuardFrame = requestAnimationFrame(guard);
      }

      stopFocusGuard() {
        cancelAnimationFrame(this.focusGuardFrame);
        this.focusGuardFrame = null;
      }

      onDocumentFocusIn(event) {
        if (!this.focusTrapActive || this.contains(event.target)) return;

        event.stopPropagation();
        this.recoverFocus();
      }

      onWindowBlur() {
        requestAnimationFrame(() => {
          const activeElement = document.activeElement;
          if (activeElement instanceof HTMLIFrameElement && !this.contains(activeElement)) {
            this.recoverFocus();
          }
        });
      }

      showDeclineState() {
        if (!this.selectionPanel || !this.declinePanel) return;

        this.defaultLabelledBy ||= this.getAttribute('aria-labelledby');
        this.selectionPanel.hidden = true;
        this.declinePanel.hidden = false;
        this.dataset.ageState = 'declined';

        if (this.declineHeading?.id) {
          this.setAttribute('aria-labelledby', this.declineHeading.id);
        }

        this.declineHeading?.focus({ preventScroll: true });
      }

      showSelectionState({ focus = false } = {}) {
        if (!this.selectionPanel || !this.declinePanel) return;

        this.selectionPanel.hidden = false;
        this.declinePanel.hidden = true;
        delete this.dataset.ageState;

        if (this.defaultLabelledBy) this.setAttribute('aria-labelledby', this.defaultLabelledBy);
        else this.removeAttribute('aria-labelledby');

        if (focus) this.declineButton?.focus({ preventScroll: true });
      }

      hide() {
        if (this.open && !this.ageAccepted && !Shopify.designMode) {
          this.resetGesture();
          this.focusElement?.focus({ preventScroll: true });
          return Promise.resolve();
        }

        return super.hide();
      }

      afterShow() {
        this.showSelectionState();
        if (!Shopify.designMode) this.isolateBackground();

        super.afterShow();
        if (!Shopify.designMode) this.startFocusGuard();
        this.classList.add('show-image');
      }

      afterHide() {
        this.stopFocusGuard();
        super.afterHide();
        cancelAnimationFrame(this.focusRecoveryFrame);
        this.classList.remove('show-image');
        this.restoreBackground();
        this.showSelectionState();

        // Theme-editor interactions must never create a storefront consent
        // state for the merchant's browser session.
        if (Shopify.designMode) {
          this.ageAccepted = false;
          return;
        }

        if (this.testMode) {
          this.removeCookie(this.cookieName);
          this.ageAccepted = false;
          return;
        }

        if (this.ageAccepted) this.setCookie(this.cookieName, this.expiry);
        this.ageAccepted = false;
      }

      isolateBackgroundElement(element) {
        if (!(element instanceof HTMLElement)) return;
        if (this.contains(element) || element === this.sectionRoot || element.contains(this)) return;
        if (['SCRIPT', 'STYLE', 'LINK', 'META'].includes(element.tagName)) return;
        if (this.backgroundState.has(element)) return;

        const hardIsolate =
          element instanceof HTMLIFrameElement || element.localName === 'shopify-account';

        this.backgroundState.set(element, {
          inert: element.inert,
          ariaHidden: element.getAttribute('aria-hidden'),
          tabIndex: element.getAttribute('tabindex'),
          hiddenAttribute: element.getAttribute('hidden'),
        });

        // Blur before aria-hidden so focus never remains inside a hidden subtree.
        if (hardIsolate) element.blur();
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
        if (hardIsolate) {
          element.tabIndex = -1;
          element.hidden = true;
          this.recoverFocus();
        }
      }

      isolateBackgroundTree(element) {
        if (!(element instanceof HTMLElement) || this.contains(element)) return;

        if (
          element.parentElement === document.body ||
          element instanceof HTMLIFrameElement ||
          element.localName === 'shopify-account'
        ) {
          this.isolateBackgroundElement(element);
        }
        element
          .querySelectorAll('iframe, shopify-account')
          .forEach((candidate) => this.isolateBackgroundElement(candidate));
      }

      isolateBackground() {
        [...document.body.children].forEach((element) => this.isolateBackgroundTree(element));

        if (this.backgroundObserver) return;
        this.backgroundObserver = new MutationObserver((mutations) => {
          if (!this.focusTrapActive) return;
          for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => this.isolateBackgroundTree(node));
          }
        });
        this.backgroundObserver.observe(document.body, { childList: true, subtree: true });
      }

      restoreBackground() {
        this.backgroundObserver?.disconnect();
        this.backgroundObserver = null;

        this.backgroundState.forEach((state, element) => {
          element.inert = state.inert;
          if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', state.ariaHidden);
          if (state.tabIndex === null) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', state.tabIndex);
          if (state.hiddenAttribute === null) element.removeAttribute('hidden');
          else element.setAttribute('hidden', state.hiddenAttribute);
        });
        this.backgroundState.clear();
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
